import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Upload,
  Download,
  MoreVertical,
  BookOpen,
  Users,
  Lock,
  Star,
  CheckSquare,
  Square
} from 'lucide-react'
import { RootState } from '../../store'
import {
  fetchDecks,
  deleteDeck,
  updateDeck
} from '../../store/slices/flashcardSlice'
import { FlashcardDeck, FlashcardFilter, CreateDeckRequest, CreateFlashcardRequest } from '../../types/flashcard'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Badge from '../../components/ui/Badge'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Modal from '../../components/ui/Modal'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import { createDeckWithCards, createCard } from '../../services/flashcardService'

interface DeckActionsProps {
  deck: FlashcardDeck
  onEdit: (deck: FlashcardDeck) => void
  onDelete: (deckId: string) => void
  onDuplicate: (deck: FlashcardDeck) => void
  onToggleVisibility: (deck: FlashcardDeck) => void
}

const DeckActions: React.FC<DeckActionsProps> = ({
  deck,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleVisibility
}) => {
  const [showMenu, setShowMenu] = useState(false)

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowMenu(!showMenu)}
      >
        <MoreVertical size={16} />
      </Button>
      
      {showMenu && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
          <div className="py-1">
            <button
              onClick={() => {
                onEdit(deck)
                setShowMenu(false)
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Edit size={14} />
              Düzenle
            </button>
            
            <button
              onClick={() => {
                onDuplicate(deck)
                setShowMenu(false)
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Copy size={14} />
              Kopyala
            </button>
            
            <button
              onClick={() => {
                onToggleVisibility(deck)
                setShowMenu(false)
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              {deck.isPublic ? <EyeOff size={14} /> : <Eye size={14} />}
              {deck.isPublic ? 'Gizle' : 'Yayınla'}
            </button>
            
            <hr className="my-1" />
            
            <button
              onClick={() => {
                onDelete(deck.id)
                setShowMenu(false)
              }}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 size={14} />
              Sil
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const AdminFlashcards: React.FC = () => {
  const dispatch = useDispatch()
  const { decks, loading, error } = useSelector((state: RootState) => state.flashcard)
  
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDecks, setSelectedDecks] = useState<string[]>([])
  const [showFilters, setShowFilters] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deckToDelete, setDeckToDelete] = useState<string | null>(null)
  const [filter, setFilter] = useState<FlashcardFilter>({
    subject: '',
    topic: '',
    difficulty: undefined,
    isPublic: undefined
  })
  
  // Import modal state & handlers
  const [showImportModal, setShowImportModal] = useState(false)
  type ImportedCard = Omit<CreateFlashcardRequest, 'deckId'>
  const [importFile, setImportFile] = useState<File | null>(null)
  const [parsedCards, setParsedCards] = useState<ImportedCard[]>([])
  const [importError, setImportError] = useState<string | null>(null)
  const [importMode, setImportMode] = useState<'new' | 'existing'>('new')
  const [newDeckForm, setNewDeckForm] = useState<CreateDeckRequest>({
    title: '',
    subject: '',
    topic: '',
    unit: '',
    isPublic: false,
    description: ''
  })
  const [targetDeckId, setTargetDeckId] = useState('')
  const [importIsProcessing, setImportIsProcessing] = useState(false)

  const resetImportState = () => {
    setImportFile(null)
    setParsedCards([])
    setImportError(null)
    setImportMode('new')
    setNewDeckForm({ title: '', subject: '', topic: '', unit: '', isPublic: false, description: '' })
    setTargetDeckId('')
    setImportIsProcessing(false)
  }

  const normalizeDifficulty = (value: any): 'easy' | 'medium' | 'hard' => {
    const v = String(value || '').toLowerCase().trim()
    if (v === 'easy' || v === 'kolay') return 'easy'
    if (v === 'hard' || v === 'zor') return 'hard'
    return 'medium'
  }

  const toImportedCard = (row: any): ImportedCard | null => {
    const question = (row.question ?? row.soru ?? '').toString().trim()
    const answer = (row.answer ?? row.cevap ?? '').toString().trim()
    if (!question || !answer) return null
    const tagsRaw = row.tags ?? row.etiketler
    let tags: string[] = []
    if (Array.isArray(tagsRaw)) {
      tags = tagsRaw.map((t) => String(t).trim()).filter(Boolean)
    } else if (typeof tagsRaw === 'string') {
      tags = tagsRaw.split(',').map((t: string) => t.trim()).filter(Boolean)
    }
    return {
      question,
      answer,
      hint: row.hint ?? row.ipucu ? String(row.hint ?? row.ipucu) : undefined,
      explanation: row.explanation ?? row.aciklama ? String(row.explanation ?? row.aciklama) : undefined,
      image: row.image ? String(row.image) : undefined,
      formula: row.formula ? String(row.formula) : undefined,
      difficulty: normalizeDifficulty(row.difficulty ?? row.zorluk),
      tags
    }
  }

  const parseCSVFile = (file: File): Promise<ImportedCard[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            const rows = (results.data as any[]).filter(Boolean)
            const cards = rows.map(toImportedCard).filter(Boolean) as ImportedCard[]
            resolve(cards)
          } catch (e: any) {
            reject(e)
          }
        },
        error: (err) => reject(err)
      })
    })
  }

  const parseXLSXFile = async (file: File): Promise<ImportedCard[]> => {
    const data = await file.arrayBuffer()
    const workbook = XLSX.read(data, { type: 'array' })
    const sheetName = workbook.SheetNames[0]
    const sheet = workbook.Sheets[sheetName]
    const rows = XLSX.utils.sheet_to_json<any>(sheet, { defval: '' })
    const cards = rows.map(toImportedCard).filter(Boolean) as ImportedCard[]
    return cards
  }

  const parseJSONFile = async (file: File): Promise<ImportedCard[]> => {
    const text = await file.text()
    const json = JSON.parse(text)
    const rows: any[] = Array.isArray(json) ? json : (Array.isArray(json?.cards) ? json.cards : [])
    const cards = rows.map(toImportedCard).filter(Boolean) as ImportedCard[]
    return cards
  }

  const onSelectFile = async (file: File | null) => {
    try {
      setImportError(null)
      setParsedCards([])
      setImportFile(file)
      if (!file) return
      const ext = file.name.split('.').pop()?.toLowerCase()
      let cards: ImportedCard[] = []
      if (ext === 'csv') {
        cards = await parseCSVFile(file)
      } else if (ext === 'xlsx' || ext === 'xls') {
        cards = await parseXLSXFile(file)
      } else if (ext === 'json') {
        cards = await parseJSONFile(file)
      } else {
        throw new Error('Desteklenmeyen dosya türü')
      }
      if (cards.length === 0) {
        setImportError('Geçerli kart bulunamadı. Lütfen şablon sütunlarını kontrol edin (question, answer, vb.)')
      }
      setParsedCards(cards)
    } catch (err: any) {
      console.error('Import parse error:', err)
      setImportError(err?.message || 'Dosya ayrıştırılırken hata oluştu')
    }
  }

  const handleStartImport = async () => {
    try {
      setImportError(null)
      if (parsedCards.length === 0) {
        setImportError('İçe aktarılacak kart bulunmuyor')
        return
      }
      setImportIsProcessing(true)

      if (importMode === 'new') {
        if (!newDeckForm.title || !newDeckForm.subject || !newDeckForm.topic || !newDeckForm.unit) {
          setImportError('Lütfen yeni deste için başlık, ders, konu ve ünite alanlarını doldurun')
          setImportIsProcessing(false)
          return
        }
        await createDeckWithCards(newDeckForm, parsedCards)
      } else {
        if (!targetDeckId) {
          setImportError('Lütfen hedef desteyi seçin')
          setImportIsProcessing(false)
          return
        }
        for (const card of parsedCards) {
          await createCard({ ...card, deckId: targetDeckId })
        }
      }

      // Başarılı: kapat ve yenile
      setShowImportModal(false)
      resetImportState()
      await dispatch(fetchDecks() as any)
    } catch (err: any) {
      console.error('Import error:', err)
      setImportError(err?.message || 'İçe aktarma sırasında bir hata oluştu')
    } finally {
      setImportIsProcessing(false)
    }
  }
  
  // LGS subjects
  const subjects = [
    { value: 'matematik', label: 'Matematik' },
    { value: 'turkce', label: 'Türkçe' },
    { value: 'fen', label: 'Fen Bilimleri' },
    { value: 'sosyal', label: 'Sosyal Bilgiler' },
    { value: 'ingilizce', label: 'İngilizce' },
    { value: 'din', label: 'Din Kültürü' }
  ]
  
  useEffect(() => {
    dispatch(fetchDecks() as any)
  }, [])
  
  const filteredDecks = decks.filter(deck => {
    const matchesSearch = deck.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         deck.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         deck.subject.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesSubject = !filter.subject || deck.subject === filter.subject
    const matchesTopic = !filter.topic || deck.topic === filter.topic
    const matchesDifficulty = !filter.difficulty || deck.difficulty === filter.difficulty
    const matchesVisibility = filter.isPublic === undefined || deck.isPublic === filter.isPublic
    
    return matchesSearch && matchesSubject && matchesTopic && matchesDifficulty && matchesVisibility
  })
  
  const handleSelectDeck = (deckId: string) => {
    setSelectedDecks(prev => 
      prev.includes(deckId) 
        ? prev.filter(id => id !== deckId)
        : [...prev, deckId]
    )
  }
  
  const handleSelectAll = () => {
    if (selectedDecks.length === filteredDecks.length) {
      setSelectedDecks([])
    } else {
      setSelectedDecks(filteredDecks.map(deck => deck.id))
    }
  }
  
  const handleDeleteDeck = async (deckId: string) => {
    setDeckToDelete(deckId)
    setShowDeleteModal(true)
  }
  
  const confirmDelete = async () => {
    if (deckToDelete) {
      try {
        await dispatch(deleteDeck(deckToDelete) as any)
        setShowDeleteModal(false)
        setDeckToDelete(null)
      } catch (error) {
        console.error('Error deleting deck:', error)
      }
    }
  }
  
  const handleBulkDelete = async () => {
    if (selectedDecks.length === 0) return
    
    try {
      for (const deckId of selectedDecks) {
        await dispatch(deleteDeck(deckId) as any)
      }
      setSelectedDecks([])
    } catch (error) {
      console.error('Error bulk deleting decks:', error)
    }
  }
  
  const handleToggleVisibility = async (deck: FlashcardDeck) => {
    try {
      await dispatch(updateDeck({
        deckId: deck.id,
        updates: { isPublic: !deck.isPublic }
      }) as any)
    } catch (error) {
      console.error('Error updating deck visibility:', error)
    }
  }
  
  const handleDuplicateDeck = async (deck: FlashcardDeck) => {
    // TODO: Implement deck duplication
    console.log('Duplicating deck:', deck.id)
  }
  
  const getSubjectLabel = (subject: string) => {
    const subjectData = subjects.find(s => s.value === subject)
    return subjectData?.label || subject
  }
  
  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'Kolay'
      case 'medium': return 'Orta'
      case 'hard': return 'Zor'
      default: return difficulty
    }
  }
  
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'hard': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Flashcard Yönetimi
              </h1>
              <p className="text-gray-600">
                Flashcard destelerini ve kartlarını yönet
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => { setShowImportModal(true); }}>
                <Upload size={16} className="mr-2" />
                İçe Aktar
              </Button>
              
              <Button variant="outline" size="sm">
                <Download size={16} className="mr-2" />
                Dışa Aktar
              </Button>
              
              <Link to="/admin/flashcards/create">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus size={16} className="mr-2" />
                  Yeni Deste
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Deste ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-blue-50 border-blue-200' : ''}
            >
              <Filter size={16} className="mr-2" />
              Filtrele
            </Button>
          </div>
          
          {/* Filter Panel */}
          {showFilters && (
            <Card className="p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ders
                  </label>
                  <select
                    value={filter.subject || ''}
                    onChange={(e) => setFilter(prev => ({ ...prev, subject: e.target.value || undefined }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tüm Dersler</option>
                    {subjects.map(subject => (
                      <option key={subject.value} value={subject.value}>
                        {subject.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zorluk
                  </label>
                  <select
                    value={filter.difficulty || ''}
                    onChange={(e) => setFilter(prev => ({ ...prev, difficulty: e.target.value as any || undefined }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tüm Seviyeler</option>
                    <option value="easy">Kolay</option>
                    <option value="medium">Orta</option>
                    <option value="hard">Zor</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Görünürlük
                  </label>
                  <select
                    value={filter.isPublic === undefined ? '' : filter.isPublic ? 'public' : 'private'}
                    onChange={(e) => {
                      const value = e.target.value
                      setFilter(prev => ({ 
                        ...prev, 
                        isPublic: value === '' ? undefined : value === 'public' 
                      }))
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tümü</option>
                    <option value="public">Yayında</option>
                    <option value="private">Taslak</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    onClick={() => setFilter({
                      subject: '',
                      topic: '',
                      difficulty: undefined,
                      isPublic: undefined
                    })}
                    className="w-full"
                  >
                    Temizle
                  </Button>
                </div>
              </div>
            </Card>
          )}
          
          {/* Bulk Actions */}
          {selectedDecks.length > 0 && (
            <Card className="p-4 mb-4 bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-800">
                  {selectedDecks.length} deste seçildi
                </span>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkDelete}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 size={14} className="mr-1" />
                    Sil
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedDecks([])}
                  >
                    İptal
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
        
        {/* Error State */}
        {error && (
          <Card className="mb-6 p-4 bg-red-50 border-red-200">
            <p className="text-red-800">{error}</p>
          </Card>
        )}
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg mr-3">
                <BookOpen className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Toplam Deste</p>
                <p className="text-2xl font-bold text-gray-900">{decks.length}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg mr-3">
                <Eye className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Yayında</p>
                <p className="text-2xl font-bold text-gray-900">
                  {decks.filter(deck => deck.isPublic).length}
                </p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg mr-3">
                <Lock className="text-yellow-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Taslak</p>
                <p className="text-2xl font-bold text-gray-900">
                  {decks.filter(deck => !deck.isPublic).length}
                </p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg mr-3">
                <Users className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Toplam Kart</p>
                <p className="text-2xl font-bold text-gray-900">
                  {decks.reduce((total, deck) => total + deck.totalCards, 0)}
                </p>
              </div>
            </div>
          </Card>
        </div>
        
        {/* Decks Table */}
        {filteredDecks.length === 0 ? (
          <Card className="p-12 text-center">
            <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery || Object.values(filter).some(v => v) 
                ? 'Arama kriterlerine uygun deste bulunamadı' 
                : 'Henüz deste oluşturulmamış'
              }
            </h3>
            <p className="text-gray-600 mb-6">
              {searchQuery || Object.values(filter).some(v => v)
                ? 'Farklı anahtar kelimeler deneyin veya filtreleri değiştirin'
                : 'İlk flashcard desteni oluşturarak başla'
              }
            </p>
            {!searchQuery && !Object.values(filter).some(v => v) && (
              <Link to="/admin/flashcards/create">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus size={16} className="mr-2" />
                  İlk Desteni Oluştur
                </Button>
              </Link>
            )}
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={handleSelectAll}
                        className="flex items-center gap-2"
                      >
                        {selectedDecks.length === filteredDecks.length ? (
                          <CheckSquare className="text-blue-600" size={16} />
                        ) : (
                          <Square className="text-gray-400" size={16} />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Deste
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ders
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kart Sayısı
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Zorluk
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Güncelleme
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDecks.map((deck) => (
                    <tr key={deck.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleSelectDeck(deck.id)}
                        >
                          {selectedDecks.includes(deck.id) ? (
                            <CheckSquare className="text-blue-600" size={16} />
                          ) : (
                            <Square className="text-gray-400" size={16} />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                            <BookOpen className="text-blue-600" size={16} />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {deck.title}
                            </div>
                            {deck.description && (
                              <div className="text-sm text-gray-500 truncate max-w-xs">
                                {deck.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className="bg-blue-100 text-blue-800">
                          {getSubjectLabel(deck.subject)}
                        </Badge>
                        {deck.topic && (
                          <div className="text-xs text-gray-500 mt-1">
                            {deck.topic}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {deck.totalCards}
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={getDifficultyColor(deck.difficulty)}>
                          {getDifficultyLabel(deck.difficulty)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {deck.isPublic ? (
                            <Badge className="bg-green-100 text-green-800">
                              <Eye size={12} className="mr-1" />
                              Yayında
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">
                              <Lock size={12} className="mr-1" />
                              Taslak
                            </Badge>
                          )}
                          {deck.isFavorite && (
                            <Star className="text-yellow-500 fill-current" size={14} />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(deck.updatedAt).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Link to={`/admin/flashcards/edit/${deck.id}`}>
                            <Button variant="outline" size="sm">
                              <Edit size={14} />
                            </Button>
                          </Link>
                          
                          <DeckActions
                            deck={deck}
                            onEdit={(deck) => console.log('Edit deck:', deck.id)}
                            onDelete={handleDeleteDeck}
                            onDuplicate={handleDuplicateDeck}
                            onToggleVisibility={handleToggleVisibility}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Desteyi Sil"
      >
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Bu desteyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve 
            deste içindeki tüm kartlar da silinecektir.
          </p>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              İptal
            </Button>
            <Button
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </Button>
          </div>
        </div>
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => { setShowImportModal(false); resetImportState() }}
        title="Flashcard İçe Aktar"
      >
        <div className="p-6 space-y-6">
          <div>
            <p className="text-sm text-gray-600">
              Desteklenen formatlar: CSV, Excel (.xlsx, .xls) ve JSON. Gerekli sütunlar: question, answer. 
              İsteğe bağlı sütunlar: hint, explanation, difficulty (easy|medium|hard), tags (virgülle ayırın).
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Dosya</label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.json"
              onChange={(e) => onSelectFile(e.target.files?.[0] || null)}
              className="w-full"
            />
            {importFile && (
              <div className="text-sm text-gray-600">Seçili dosya: {importFile.name}</div>
            )}
            {parsedCards.length > 0 && (
              <div className="text-sm text-green-700">Önizleme: {parsedCards.length} kart tespit edildi</div>
            )}
            {importError && (
              <div className="text-sm text-red-600">{importError}</div>
            )}
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Hedef</label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={importMode === 'new'}
                  onChange={() => setImportMode('new')}
                />
                Yeni Deste Oluştur
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={importMode === 'existing'}
                  onChange={() => setImportMode('existing')}
                />
                Mevcut Desteye Ekle
              </label>
            </div>
          </div>

          {importMode === 'new' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Deste Başlığı</label>
                <input
                  type="text"
                  value={newDeckForm.title}
                  onChange={(e) => setNewDeckForm({ ...newDeckForm, title: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Örn. Matematik - Oran Orantı"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Ders</label>
                <select
                  value={newDeckForm.subject}
                  onChange={(e) => setNewDeckForm({ ...newDeckForm, subject: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Seçiniz</option>
                  <option value="matematik">Matematik</option>
                  <option value="turkce">Türkçe</option>
                  <option value="fen">Fen Bilimleri</option>
                  <option value="sosyal">Sosyal Bilgiler</option>
                  <option value="ingilizce">İngilizce</option>
                  <option value="din">Din Kültürü</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Konu</label>
                <input
                  type="text"
                  value={newDeckForm.topic}
                  onChange={(e) => setNewDeckForm({ ...newDeckForm, topic: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Örn. Cebir"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Ünite</label>
                <input
                  type="text"
                  value={newDeckForm.unit}
                  onChange={(e) => setNewDeckForm({ ...newDeckForm, unit: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Örn. Oran Orantı"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Açıklama (opsiyonel)</label>
                <input
                  type="text"
                  value={newDeckForm.description}
                  onChange={(e) => setNewDeckForm({ ...newDeckForm, description: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Kısa açıklama"
                />
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newDeckForm.isPublic}
                    onChange={(e) => setNewDeckForm({ ...newDeckForm, isPublic: e.target.checked })}
                  />
                  Yayınla (herkese açık)
                </label>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700">Hedef Deste</label>
              <select
                value={targetDeckId}
                onChange={(e) => setTargetDeckId(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Seçiniz</option>
                {decks.map((d) => (
                  <option key={d.id} value={d.id}>{d.title}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Şablon sütunları: question, answer, hint, explanation, difficulty, tags
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowImportModal(false); resetImportState() }}>İptal</Button>
              <Button onClick={handleStartImport} disabled={importIsProcessing || parsedCards.length === 0} className="bg-blue-600 hover:bg-blue-700">
                {importIsProcessing ? 'Aktarılıyor...' : 'İçe Aktar'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AdminFlashcards